import type { CustomRouteActivityType, CustomRouteMap } from '@alltrails/types/CustomRoute';
import type { CustomRouteDetails } from '../../types/CustomRoute';
import getSegmentsForMap from './getSegmentsForMap';

export type MapResponse = { maps: CustomRouteMap[]; meta: { status: string; items: number; timestamp: string } };

export type CustomRouteResponse = {
  route: CustomRouteDetails;
  map: CustomRouteMap;
};

export const transformCustomRouteMap = (map: CustomRouteMap): CustomRouteResponse => {
  const {
    id,
    slug,
    bounds: rawBounds,
    name: rawName,
    description: rawDescription,
    contentPrivacy,
    activity,
    summaryStats,
    estimatedTimeToComplete,
    metadata,
    location
  } = map;
  const activityType = (activity?.uid as CustomRouteActivityType) ?? null;
  const { distanceTotal: distance, elevationGain, elevationLoss } = summaryStats;
  const { created: createdAt } = metadata;
  const name = rawName || undefined;
  const description = rawDescription || undefined;
  const estimatedTime = estimatedTimeToComplete || undefined;

  const bounds = rawBounds
    ? {
        longitudeTopLeft: Number(rawBounds.longitudeTopLeft),
        latitudeTopLeft: Number(rawBounds.latitudeTopLeft),
        longitudeBottomRight: Number(rawBounds.longitudeBottomRight),
        latitudeBottomRight: Number(rawBounds.latitudeBottomRight)
      }
    : undefined;

  const segments = getSegmentsForMap(map);

  const waypoints = [...map.waypoints]
    .sort((a, b) => a.order - b.order)
    .map(waypoint => {
      const { id: serverId, order, name, description, location, waypointDisplayProperty, waypointCategory, contentPrivacy, isGlobal } = waypoint;
      const id = (order || 1) - 1;
      const { latitude, longitude } = location;
      const { showTitle } = waypointDisplayProperty;

      return {
        id,
        serverId,
        name,
        description,
        latitude,
        longitude,
        showTitle,
        waypointCategory: waypointCategory ?? undefined,
        contentPrivacy,
        isGlobal
      };
    });

  return {
    route: {
      id,
      slug,
      bounds,
      name,
      description,
      contentPrivacy,
      activityType,
      distance,
      elevationGain,
      elevationLoss,
      estimatedTime,
      segments,
      waypoints,
      createdAt,
      location
    },
    map
  };
};

export const transformCustomRouteMapResponse = (response: Omit<MapResponse, 'meta'>): CustomRouteResponse => {
  const map = response.maps[0];
  return transformCustomRouteMap(map);
};
